import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

// Utilitaires pour WebAuthn
const arrayBufferToBase64 = (buffer) => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
};

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const useBiometricAuth = () => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    checkAvailability();
    checkIfEnabled();
  }, []);

  const checkAvailability = () => {
    // Vérifie si WebAuthn est disponible
    const available = window.PublicKeyCredential !== undefined && 
                     navigator.credentials !== undefined;
    setIsAvailable(available);
  };

  const checkIfEnabled = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await api.get('/auth/biometric-status');
        setIsEnabled(response.data.enabled);
      }
    } catch (error) {
      console.log('Not logged in or error checking biometric status');
    }
  };

  const registerBiometric = async () => {
    if (!isAvailable) {
      toast.error('Authentification biométrique non disponible');
      return false;
    }

    try {
      // Générer un challenge aléatoire
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyOptions = {
        challenge: challenge,
        rp: {
          name: 'Bytransport',
          id: window.location.hostname
        },
        user: {
          id: new Uint8Array(16),
          name: localStorage.getItem('userEmail') || 'user@bytransport.fr',
          displayName: localStorage.getItem('userName') || 'Utilisateur'
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },  // ES256
          { type: 'public-key', alg: -257 } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required'
        },
        timeout: 60000,
        attestation: 'none'
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      });

      if (credential) {
        // Envoyer les credentials au serveur
        const credentialData = {
          credentialId: arrayBufferToBase64(credential.rawId),
          publicKey: arrayBufferToBase64(credential.response.getPublicKey())
        };

        await api.post('/auth/register-biometric', credentialData);
        
        setIsEnabled(true);
        toast.success('Touch ID / Face ID activé !');
        return true;
      }
    } catch (error) {
      console.error('Erreur enregistrement biométrique:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Authentification biométrique refusée');
      } else {
        toast.error('Erreur lors de l\'activation');
      }
      return false;
    }
  };

  const authenticateWithBiometric = async () => {
    if (!isAvailable) {
      toast.error('Authentification biométrique non disponible');
      return null;
    }

    try {
      // Récupérer le credential ID stocké
      const credentialId = localStorage.getItem('biometricCredentialId');
      
      if (!credentialId) {
        toast.error('Aucune authentification biométrique configurée');
        return null;
      }

      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyOptions = {
        challenge: challenge,
        allowCredentials: [{
          type: 'public-key',
          id: base64ToArrayBuffer(credentialId)
        }],
        userVerification: 'required',
        timeout: 60000
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });

      if (assertion) {
        // Vérifier avec le serveur
        const response = await api.post('/auth/verify-biometric', {
          credentialId: arrayBufferToBase64(assertion.rawId)
        });

        if (response.data.access_token) {
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success('Connexion biométrique réussie !');
          return response.data;
        }
      }
    } catch (error) {
      console.error('Erreur authentification biométrique:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Authentification biométrique annulée');
      } else {
        toast.error('Échec de l\'authentification biométrique');
      }
      return null;
    }
  };

  return {
    isAvailable,
    isEnabled,
    registerBiometric,
    authenticateWithBiometric,
    checkIfEnabled
  };
};
